<template>
  <div class="review-form">
    <h4 class="mb-3">
      <i class="fas fa-star text-warning me-2"></i>
      Share Your Experience
    </h4>
    
    <form @submit.prevent="submitReview" class="review-form-content">
      <!-- Name Field (Optional) -->
      <div class="mb-3">
        <label for="reviewName" class="form-label">
          Name <span class="text-muted">(Optional)</span>
        </label>
        <input
          type="text"
          class="form-control"
          id="reviewName"
          v-model="review.name"
          placeholder="Your name (or leave blank to post as Anonymous)"
          maxlength="100"
        >
      </div>

      <!-- Email Field (Optional) -->
      <div class="mb-3">
        <label for="reviewEmail" class="form-label">
          Email <span class="text-muted">(Optional)</span>
        </label>
        <input
          type="email"
          class="form-control"
          id="reviewEmail"
          v-model="review.email"
          placeholder="Your email (for follow-up if needed)"
        >
        <div class="form-text">
          Your email will not be displayed publicly
        </div>
      </div>

      <!-- Rating Field (Optional) -->
      <div class="mb-3">
        <label class="form-label">
          Rating <span class="text-muted">(Optional)</span>
        </label>
        <div class="rating-stars">
          <span
            v-for="star in 5"
            :key="star"
            class="star"
            :class="{ 'filled': star <= review.rating }"
            @click="setRating(star)"
            @mouseover="hoverRating = star"
            @mouseleave="hoverRating = 0"
          >
            <i class="fas fa-star"></i>
          </span>
          <span v-if="review.rating" class="ms-2 text-muted">
            {{ review.rating }} / 5 stars
          </span>
        </div>
      </div>

      <!-- Content Field (Required) -->
      <div class="mb-3">
        <label for="reviewContent" class="form-label">
          Your Review/Comment <span class="text-danger">*</span>
        </label>
        <textarea
          class="form-control"
          id="reviewContent"
          v-model="review.content"
          rows="4"
          placeholder="Share your experience with TutorMed..."
          required
          maxlength="2000"
        ></textarea>
        <div class="form-text">
          {{ review.content.length }}/2000 characters
        </div>
      </div>

      <!-- Submit Button -->
      <div class="d-flex justify-content-end align-items-center">
        <button
          type="submit"
          class="btn btn-primary"
          :disabled="!review.content.trim() || isSubmitting"
        >
          <span v-if="isSubmitting">
            <i class="fas fa-spinner fa-spin me-2"></i>
            Submitting...
          </span>
          <span v-else>
            <i class="fas fa-paper-plane me-2"></i>
            Submit Review
          </span>
        </button>
      </div>
    </form>

    <!-- Success Message -->
    <div v-if="showSuccess" class="alert alert-success mt-3" role="alert">
      <i class="fas fa-check-circle me-2"></i>
      Thank you for your review! It will be published after moderation.
    </div>

    <!-- Error Message -->
    <div v-if="errorMessage" class="alert alert-danger mt-3" role="alert">
      <i class="fas fa-exclamation-circle me-2"></i>
      {{ errorMessage }}
    </div>
  </div>
</template>

<script>
import api from '@/services/api'

export default {
  name: 'ReviewForm',
  props: {
    linkToken: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      review: {
        name: '',
        email: '',
        content: '',
        rating: null
      },
      hoverRating: 0,
      isSubmitting: false,
      showSuccess: false,
      errorMessage: ''
    }
  },
  methods: {
    setRating(rating) {
      this.review.rating = this.review.rating === rating ? null : rating
    },
    async submitReview() {
      if (!this.review.content.trim()) {
        this.errorMessage = 'Please enter your review or comment'
        return
      }

      this.isSubmitting = true
      this.errorMessage = ''
      this.showSuccess = false

      try {
        const payload = { ...this.review }

        // Include link token if provided
        if (this.linkToken) {
          payload.link_token = this.linkToken
        }

        await api.post('/reviews/', payload)
        
        // Reset form
        this.review = {
          name: '',
          email: '',
          content: '',
          rating: null
        }
        
        this.showSuccess = true
        this.$emit('review-submitted')
        
        // Hide success message after 5 seconds
        setTimeout(() => {
          this.showSuccess = false
        }, 5000)
        
      } catch (error) {
        console.error('Error submitting review:', error)
        this.errorMessage = error.response?.data?.detail || 'Failed to submit review. Please try again.'
      } finally {
        this.isSubmitting = false
      }
    }
  }
}
</script>

<style scoped>
.review-form {
  background: #f8f9fa;
  border-radius: 12px;
  padding: 2rem;
  border: 1px solid #e9ecef;
}

.review-form-content {
  max-width: 600px;
}

.rating-stars {
  display: flex;
  align-items: center;
}

.star {
  font-size: 1.5rem;
  color: #ddd;
  cursor: pointer;
  transition: color 0.2s ease;
  margin-right: 0.25rem;
}

.star:hover,
.star.filled {
  color: #ffc107;
}

.form-control:focus {
  border-color: #007bff;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

.btn-primary {
  background: linear-gradient(135deg, #007bff, #0056b3);
  border: none;
  border-radius: 8px;
  padding: 0.75rem 1.5rem;
  font-weight: 500;
  transition: all 0.3s ease;
}

.btn-primary:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 123, 255, 0.3);
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.alert {
  border-radius: 8px;
  border: none;
}

.alert-success {
  background: linear-gradient(135deg, #d4edda, #c3e6cb);
  color: #155724;
}

.alert-danger {
  background: linear-gradient(135deg, #f8d7da, #f5c6cb);
  color: #721c24;
}

@media (max-width: 768px) {
  .review-form {
    padding: 1.5rem;
    margin: 0 1rem;
  }
  
  .d-flex {
    flex-direction: column;
    align-items: stretch !important;
  }
  
  .btn-primary {
    margin-top: 1rem;
  }
}
</style>